import { Translation } from 'react-i18next'

import { CategoryTitleCell } from 'components/FieldRenderers/CategoryTitleCell'
import { Flag } from 'components/Flag'
import { UNDEFINED_VALUE } from 'const/formatting'
import { isUnset } from 'helpers/utilities'
import { CellRenderProps, Field } from 'types'

import { createField } from './createField'
import { FieldCreatorOptions } from './localTypes'

interface Category {
    id?: string
    country_code?: string
}

interface CategoryRecord {
    category?: Category
}

interface NextLevelCategoryRecord {
    next_level_category?: Category
}

interface RootCategoryRecord {
    root_category?: Category
}

export function categoryCountryCode<RecordType extends CategoryRecord>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const dataIndex = options.dataIndex ?? ['country_code']
    const name = (
        <Translation>
            {(t) => t('common:marketplace', 'Marketplace')}
        </Translation>
    )
    return createField({
        ...options,
        id: 'country_code',
        name,
        shortName: name,
        dataIndex,
        renderOptions: options.renderOptions ?? {
            render: ({ value }) =>
                isUnset(value) ? (
                    UNDEFINED_VALUE
                ) : (
                    <span className="fg-flag-container">
                        <Flag country={value} />
                        <div>{value.toUpperCase()}</div>
                    </span>
                ),
        },
        minWidth: 85,
        width: 85,
    })
}

export function categoryNodeId<RecordType extends CategoryRecord>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const dataIndex = options.dataIndex ?? ['category_node_id']
    const name = (
        <Translation>
            {(t) => t('common:subcategory', 'Subcategory')}
        </Translation>
    )
    return createField({
        ...options,
        id: 'category_node_id',
        name,
        shortName: name,
        minWidth: 100,
        dataIndex,
        renderOptions: options.renderOptions ?? {
            render: ({ value, record }: CellRenderProps<RecordType>) => (
                <CategoryTitleCell
                    value={value}
                    categoryId={record.category?.id}
                    marketplace={record.category?.country_code}
                />
            ),
        },
        width: 200,
    })
}

export function nextLevelCategoryId<RecordType extends NextLevelCategoryRecord>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const name = (
        <Translation>
            {(t) => t('common:nextLevelCategory', 'Subcategory')}
        </Translation>
    )
    return createField({
        ...options,
        id: 'next_level_category_id',
        name,
        shortName: name,
        minWidth: 100,
        dataIndex: options.dataIndex ?? ['next_level_category_name'],
        renderOptions: options.renderOptions ?? {
            render: ({ value, record }: CellRenderProps<RecordType>) => (
                <CategoryTitleCell
                    value={value}
                    categoryId={record.next_level_category?.id}
                    marketplace={record.next_level_category?.country_code}
                />
            ),
        },
        width: 200,
    })
}

export function rootCategoryId<RecordType extends RootCategoryRecord>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const dataIndex = options.dataIndex ?? ['root_category_id']
    const name = (
        <Translation>
            {(t) => t('common:rootCategory', 'Parent Category')}
        </Translation>
    )
    return createField({
        ...options,
        id: 'root_category_id',
        name,
        shortName: name,
        minWidth: 100,
        dataIndex,
        renderOptions: options.renderOptions ?? {
            render: ({ value, record }: CellRenderProps<RecordType>) => (
                <CategoryTitleCell
                    value={value}
                    categoryId={record.root_category?.id}
                    marketplace={record.root_category?.country_code}
                />
            ),
        },
        width: 200,
    })
}
